<template>
  <FilterNoResults
    v-if="showNoResultsNotice"
    @on-close="showNoResultsNotice = false"
  />
  <!-- Sticky + breakout on same wrapper: sticky must not share a node with overflow-hidden (breaks WebKit/Chromium). -->
  <div
    class="filters-bar -mx-4 md:mx-0 sticky top-[110px] md:top-[--kft-navbar-height] bg-white sm:block"
    :class="zIndex"
  >
    <ClientOnly>
      <template #fallback>
        <div class="px-4 md:px-0">
          <FilterBarSkeleton />
        </div>
      </template>
      <div class="px-4 md:px-0">
        <div class="filter-wrap">
          <Filters
            :facets="filterOptions"
            :filters="filters"
            @filters-updated="handleFiltersUpdate"
          />

          <FiltersSelected
            :location-label="currentLocality?.name"
            @filters-updated="handleFiltersUpdate"
          />
        </div>
        <div class="flex justify-between mt-0 pb-2">
          <FilterResultCount
            class="text-xs text-gray-500"
            :hits-length="searchResults?.nbHits"
            :filters="{
              ...filters,
              locality: {
                name: filters.location,
              },
              categories: filters.categories,
            }"
            :current-locality="currentLocality?.name"
            :is-no-results="showNoResultsNotice"
          />
          <FilterOrderBy />
        </div>
      </div>
    </ClientOnly>
  </div>
  <!-- :query-id="topEventsQueryID" -->
  <ProductShowcase
    id="filteredProductShowCasePageTop"
    :algolia-insights-client="algoliaInsightsClient"
    data-test-id="top-events-section"
    :products="searchResults?.hits || []"
    image-loading="lazy"
    :tags="[]"
    :loading="loading"
    :algolia-insights-label="algoliaEventName"
    :use-h-tag="false"
    :query-id="algoliaQueryId"
    :showcase-container-data-test-id="'filtered-product-showcase-results'"
    :special-giftcard-event="specialGiftcardEvents[0]"
    class="mt-4"
  >
    <template #promotion2>
      <ProductCard
        class="promotion-card cursor-pointer"
        :title="$t('general.promotionCards.bookingSolutionGiftcard.label')"
        :link="'/giftcard'"
        :tags="
          $t('product.promo.giftcard', { eventCount: eventsCount }).split('; ')
        "
        image="/_nuxt3-static/promo-cards/giftbox.webp"
        is-promo-card
      />
    </template>
    <template #promotion1>
      <ErrorBoundary>
        <ProductCard
          class="promotion-card cursor-pointer"
          :title="$t('general.promotionCards.teamevents.label')"
          link="javascript:void(0)"
          :tags="$t('product.promo.teamevent').split('; ')"
          image="/_nuxt3-static/promo-cards/team-event.webp"
          is-promo-card
          @click="toggleDateRequestSidebar(route.name, 'search_results')"
        />
      </ErrorBoundary>
    </template>
  </ProductShowcase>
  <FilterPagination
    :hits-per-page="searchResults?.hitsPerPage ?? 0"
    :nb-hits="searchResults?.nbHits ?? 0"
    :nb-pages="searchResults?.nbPages"
    @updated="handleChangePage"
  />
</template>
<script setup lang="ts">
import type { Hit } from '@nuxtjs/algolia'
import { useRouteParams, useRouteQuery } from '@vueuse/router'
import isEqual from 'lodash/isEqual'
import cloneDeep from 'lodash/cloneDeep'
import useAlgoliaEventSchema from '~/composables/schemaOrg/useAlgoliaEventSchema'
import useTransactionalPageHead from '~/composables/transactionalPage/useTransactionalPageHead'
import {
  useTransactionalPageAlgoliaSearch,
  useTransactionalPageAlgoliaSearchSideEffects,
} from '~/composables/useTransactionalPageAlgolia'
import type { Category } from '~/composables/types/event'
import {
  EventListPageType,
  type StrapiEventListPage,
} from '~/composables/types/strapiEventListPage'

import type {
  AlgoliaLocation,
  AlgoliaEventItem,
} from '~/composables/types/algolia'
import {
  buildAlgoliaAnalyticsTags,
  getAlgoliaSearchOptions,
  getListPageFederatedAnalyticsTags,
} from '~/utils/algoliaHelpers'
import { consumeFederatedSearchAnalyticsOutcome } from '~/utils/federatedSearchAnalyticsSession'
import type { SearchFilterOptions } from '~/composables/useSearchFilters'
import { useTransactionalProductSchema } from '~/composables/schemaOrg/useTransactionalProductSchema'
import useAlgoliaEventCarouselSchema from '~/composables/schemaOrg/useAlgoliaEventCarouselSchema'
import { eventListPageCategoryUrl } from '~/components/transactionalPage/requestParams'
import { useAlgoliaSearchInsights } from '~/composables/algolia/useAlgoliaSearchInsights'
import { useAlgoliaIndexNames } from '~/composables/algolia/useAlgoliaIndexNames'
import { getLocationDisplayName } from '~/components/FederatedSearch/helpers'
import { HITS_PER_PAGINATION } from '~/composables/algolia/constants'
import { NUMBER_OF_EVENTS } from '~/composables/product/constants'
import { sendFilterSelected } from '~/composables/useGtmEvents'
import { getGtmContentGroup } from '~/utils/getGtmContentGroup'
import {
  localitySlugMatchesFilter,
  localitySlugForAlgoliaOptionalFilter,
} from '~/utils/localitySlugMatch'
import {
  classifyEmptyAlgoliaSearchRecovery,
  getAlgoliaLocalityRankingRawOptions,
} from '~/utils/algoliaLocalityGeoFallback'
import { useTransactionalFilterNavigationWatch } from '~/composables/transactionalPage/useTransactionalFilterNavigationWatch'
import { buildCategoryNameToSlugMapFromAlgoliaHits } from '~/utils/buildCategoryNameToSlugMapFromAlgoliaHits'
const route = useRoute()
const router = useRouter()
const gtm = useKftGtm()
const {
  public: { baseUrl, appEnv },
} = useRuntimeConfig()
const locationSlugParam = useRouteParams('location')
const locationSlugQuery = useRouteQuery('location')
const categorySlugParam = useRouteParams('category')
const tagSlugParam = useRouteParams('slug')

/** Consumed once on client when landing from federated search (sessionStorage). */
const federatedSearchOutcome = ref<string | null>(null)
if (import.meta.client) {
  federatedSearchOutcome.value = consumeFederatedSearchAnalyticsOutcome()
}
const { toggleDateRequestSidebar } = useUiState()
const { locale, t } = useI18n()

const showNoResultsNotice = ref(false)
/** After zero hits with locality optional filters, next search uses IP geo ranking. */
const isGeoFallbackAfterEmptyLocalityHits = ref(false)
const keepNoResultsNoticeOnNextFilterSearch = ref(false)
provide('noResultsVisible', showNoResultsNotice)

const locationSlug = computed(() =>
  (locationSlugQuery.value || locationSlugParam.value)?.toString()
)

const categorySlug = computed(() => categorySlugParam.value?.toString())

const props = withDefaults(
  defineProps<{
    pageType: EventListPageType
    eventListPageData?: StrapiEventListPage
    loadSchema?: boolean
    reviewsData?: {
      reviews: any[]
      averageRating: string
      reviewCount: number
    } | null
  }>(),
  {
    eventListPageData: undefined,
    loadSchema: false,
    reviewsData: null,
  }
)

const { client: algoliaInsightsClient } = useAlgoliaSearchInsights()

const PAGE_KEY = route.path

// Generate human-readable Algolia event name based on page type
const algoliaEventName = computed(() => {
  switch (props.pageType) {
    case EventListPageType.SEARCH:
      return 'Search Page'
    case EventListPageType.CITY:
      return 'City Page'
    case EventListPageType.CATEGORY:
      return 'Category Page'
    case EventListPageType.CITY_CATEGORY:
      return 'City Category Page'
    case EventListPageType.TEAMEVENT:
      return 'Teamevent Page'
    case EventListPageType.KITS:
      return 'Kits Page'
    case EventListPageType.GENERAL:
    default:
      return 'General Page'
  }
})

const eventListPageAttributes = computed(() => {
  return props.eventListPageData
})
const { locations: locationsIndex } = useAlgoliaIndexNames()

const { result: locationsAlgoliaRaw } =
  useAlgoliaIndexSearch<AlgoliaLocation>(locationsIndex)

const localitiesAlgolia = computed(() => {
  const raw = locationsAlgoliaRaw.value
  if (!raw) return null
  return {
    ...raw,
    hits: (raw.hits ?? []).map((h) => ({
      ...h,
      name: getLocationDisplayName(h, locale.value),
    })),
  }
})

provide('localitiesAlgolia', localitiesAlgolia)

const { data: categoryResults, execute: searchCategories } =
  await useKftApiFetch<{
    data: Category
  }>(eventListPageCategoryUrl({ categorySlug: categorySlug.value }))

const initialCategoryName = computed(() => categoryResults.value?.data?.name)

const indexName =
  props.pageType === EventListPageType.SEARCH
    ? getFederatedSearchIndexName(locale.value)
    : getEventListIndexName(locale.value)

const defaultFilters = computed<Partial<SearchFilterOptions>>(() => ({
  ...(initialCategoryName.value && { categories: [initialCategoryName.value] }),
  // Only use the route path param here (city slug), NOT the query param.
  // If we included the query param, `isDefaultFilter` would treat any user-selected
  // location as a "default" and strip it from the URL when other filters change.
  location: locationSlugParam.value?.toString(),
}))

const { filters } = useSearchFilters(PAGE_KEY, defaultFilters)
provide('productsFilters', filters)

const { filteredGiftcardEvents: specialGiftcardEvents } =
  await useGiftcardEvents(filters)

const currentLocality = computed(
  () =>
    localitiesAlgolia.value?.hits?.find((d) =>
      localitySlugMatchesFilter(
        d.slug,
        locationSlug.value || filters.value.location
      )
    ) as AlgoliaLocation
)

// Keep default Strapi tags in sync when navigating between transactional pages
// (e.g. teamevent A → teamevent B). A one-time init would leave the previous
// page's tags in `filters` when the new page has none.
watch(
  () => props.eventListPageData?.searchFilters?.tags,
  (tags) => {
    filters.value.tags = tags?.length ? tags.map((d) => d.label) : []
  },
  { immediate: true }
)

const { currentOrderBy, currentPage } = useLocalizedOrderBy()
const hitsPerPagination = ref(HITS_PER_PAGINATION)

const fetchLocalitiesAndCategories = async (
  value: Partial<SearchFilterOptions>,
  oldValue: Partial<SearchFilterOptions> | undefined
) => {
  // Only re-fetch when data is absent or categories have explicitly changed.
  // Skips the redundant execute() call on the initial immediate watcher trigger
  // when the payload is already hydrated from SSR.
  const categoriesChanged =
    oldValue !== undefined && !isEqual(value?.categories, oldValue?.categories)

  if (!categoryResults.value || categoriesChanged) {
    await searchCategories()
  }
}

// This updates the amount of hits per pagination
// based on the amount of special giftcard events
// to keep a consistent amount of hits per pagination
const updateAmountOfHitsPerPagination = () => {
  if (specialGiftcardEvents.value?.length > 0) {
    hitsPerPagination.value = HITS_PER_PAGINATION - 1
  } else {
    hitsPerPagination.value = HITS_PER_PAGINATION
  }
}

updateAmountOfHitsPerPagination()

const currentIndexName = computed(
  () => currentOrderBy.value?.indexName || indexName
)

const algoliaSearch = ref(
  useAlgoliaIndexSearch<AlgoliaEventItem>(currentIndexName.value)
)

const listPageAnalyticsTags = computed(() =>
  buildAlgoliaAnalyticsTags(
    appEnv,
    getListPageFederatedAnalyticsTags(federatedSearchOutcome.value)
  )
)

const algoliaFilterParams = computed(() => {
  const locationFilter = filters.value?.location
  const locationType = filters.value?.locationType
  const useRegionsOptionalFilter =
    locationType === 'region' || Boolean(locationFilter?.includes('--'))
  const slugForOptionalFilter = localitySlugForAlgoliaOptionalFilter(
    locationFilter ?? ''
  )
  const hasLocationOptionalFilter = Boolean(locationFilter)
  return getAlgoliaSearchOptions({
    filters: filters.value,
    rawOptions: {
      hitsPerPage:
        HITS_PER_PAGINATION - (specialGiftcardEvents.value.length > 0 ? 1 : 0),
      page: currentPage.value - 1,
      analyticsTags: listPageAnalyticsTags.value,
      /**
       * if online filter is active we need to show only online events
       * and ignore the locality
       */
      ...getAlgoliaLocalityRankingRawOptions({
        hasLocationOptionalFilter,
        onlineFilterActive: Boolean(filters?.value?.online),
        isGeoFallbackAfterEmptyLocalityHits:
          isGeoFallbackAfterEmptyLocalityHits.value,
        useRegionsOptionalFilter,
        slugForOptionalFilter,
        enableAroundLatLngViaIP: import.meta.client,
      }),
    },
  })
})

const {
  data: searchResults,
  status,
  execute: performSearch,
  error: searchError,
} = await useTransactionalPageAlgoliaSearch(
  algoliaSearch,
  currentIndexName,
  algoliaFilterParams,
  hitsPerPagination
)

const loading = computed(() => status.value === 'pending')

useTransactionalPageAlgoliaSearchSideEffects(
  currentIndexName,
  loading,
  algoliaSearch,
  performSearch,
  searchError
)

const algoliaQueryId = computed(() => searchResults.value?.queryID)

const filterOptions = computed(() => {
  return {
    ...searchResults.value?.facets,
    location: localitiesAlgolia.value?.hits,
  } as {
    [key: string]: any
    location: Array<Hit<AlgoliaLocation>>
  }
})

const categoryNameToSlug = computed(() =>
  buildCategoryNameToSlugMapFromAlgoliaHits(searchResults.value?.hits ?? [])
)

const isFilterOpen = ref(false)
provide('isFilterOpen', isFilterOpen)

const zIndex = computed(() => {
  return isFilterOpen.value ? 'z-20 md:z-10' : 'z-[9]'
})

const sendFilterSelectedEvent = (filters: SearchFilterOptions) => {
  for (const [key, value] of Object.entries(filters)) {
    if (key === 'priceMin' || key === 'priceMax') continue

    if (!value || (Array.isArray(value) && value.length === 0)) continue

    const formatted = Array.isArray(value) ? value.join('; ') : value
    sendFilterSelected(key, `${formatted}`)
  }

  if (filters.priceMin && filters.priceMax) {
    sendFilterSelected('price', `${filters.priceMin}-${filters.priceMax}`)
  }
}

const handleFiltersUpdate = async (
  updatedFilters: SearchFilterOptions = {}
) => {
  sendFilterSelectedEvent(updatedFilters)

  filters.value = {
    ...filters.value,
    ...updatedFilters,
  }

  const isDefaultFilter = (key: string, value: unknown): boolean => {
    const defaultValue =
      defaultFilters.value?.[key as keyof typeof defaultFilters.value]
    if (defaultValue === undefined) return false
    if (Array.isArray(defaultValue) && Array.isArray(value)) {
      return (
        defaultValue.length === value.length &&
        defaultValue.every((v, i) => v === (value as unknown[])[i])
      )
    }
    return defaultValue === value
  }

  // Delete UNDEFINED and FALSE values; skip filters that match route-param defaults.
  const cleanFilters = Object.entries(filters.value).reduce(
    (acc: Record<string, unknown>, [key, value]) => {
      if (isDefaultFilter(key, value)) return acc
      if (value !== undefined && value !== false) {
        acc[key] = value
      }
      return acc
    },
    {}
  )

  await router.push({
    query: {
      ...cleanFilters,
      page: 1,
    },
  })
}

const handleChangePage = async (page: number) => {
  await router.push({ query: { ...router.currentRoute.value.query, page } })
  await performSearch()
}

watch(
  () => router.currentRoute.value.query.page,
  (newValue, oldValue) => {
    const currentRoute = router.currentRoute.value
    if (newValue !== oldValue) {
      const pageTitle =
        typeof document !== 'undefined' ? document?.title || '' : ''
      gtm.pushToDataLayer({
        routeName: currentRoute.name,
        content_group: getGtmContentGroup(currentRoute),
        pageType: 'PageView',
        pageUrl: baseUrl + currentRoute.fullPath,
        pagePath: currentRoute.fullPath,
        pageTitle,
        event: 'nuxtRoute',
      })
    }
  }
)
const computedHits = computed(() => searchResults.value?.hits || [])

const eventCount = computed(() => searchResults.value?.nbHits)
const eventsCount = formattedNumberOfEvents(locale.value, NUMBER_OF_EVENTS)

const searchHeading = computed(
  () =>
    eventListPageAttributes.value?.searchHeading ||
    eventListPageAttributes.value?.title ||
    ''
)

if (props.loadSchema) {
  const { script: eventSchemas } = useAlgoliaEventSchema(
    searchResults.value?.hits || []
  )

  const { script: eventCarouselSchemas } = useAlgoliaEventCarouselSchema(
    eventSchemas.value.innerHTML['@graph']
  )

  const { shouldAppendTransactionalProductSchema, schemaObj } =
    await useTransactionalProductSchema({
      locationSlug,
      tagSlug: computed(() => {
        if (props.pageType === EventListPageType.TEAMEVENT)
          return `${t('eventListPage.teamEventSlugPrefix')}-${locationSlug.value}`

        return tagSlugParam.value?.toString()
      }),
      categorySlug,
      eventListPageData: props.eventListPageData,
      pageType: props.pageType,
      searchHeading,
      eventCount,
      hits: computedHits,
      reviewsData: props.reviewsData,
    })

  useTransactionalPageHead(
    props.pageType,
    props.eventListPageData!,
    {
      script: [
        ...(searchResults.value?.hits.length > 0 ? [eventCarouselSchemas] : []),
        ...(shouldAppendTransactionalProductSchema.value && eventCount.value
          ? [schemaObj]
          : []),
      ],
    },
    {
      hits: computedHits,
    }
  )
}

// Needs to be called after the filters are updated
// so that the requests work synchronously if on a page that
// does not load on server side
//
// Details on disclaimer about "server: false":
// https://nuxt.com/docs/getting-started/data-fetching#return-values
watch(
  () => defaultFilters.value,
  async (value, oldValue) => {
    if (!import.meta.client) return // We want to avoid watchers to force refetches on SSR as they might be in vain
    await fetchLocalitiesAndCategories(value, oldValue)
    await performSearch()
  },
  {
    immediate: true,
    deep: true,
  }
)

const updateFiltersStorage = () => {
  if (import.meta.client) {
    sessionStorage.setItem('filters', JSON.stringify(filters.value))
  }
}

const runTransactionalSearch = async () => {
  if (!import.meta.client) return // We want to avoid watchers to force refetches on SSR as they might be in vain

  if (keepNoResultsNoticeOnNextFilterSearch.value) {
    keepNoResultsNoticeOnNextFilterSearch.value = false
  } else {
    showNoResultsNotice.value = false
  }

  isGeoFallbackAfterEmptyLocalityHits.value = false
  updateAmountOfHitsPerPagination()
  updateFiltersStorage()
  await performSearch()

  const hits = searchResults.value?.hits
  const recovery = classifyEmptyAlgoliaSearchRecovery({
    hits,
    filtersMatchDefault: isEqual(filters.value, defaultFilters.value),
  })

  if (recovery === 'none') return

  showNoResultsNotice.value = true

  if (recovery === 'reset_filters') {
    keepNoResultsNoticeOnNextFilterSearch.value = true
    filters.value = cloneDeep(defaultFilters.value)
    return
  }

  isGeoFallbackAfterEmptyLocalityHits.value = true
  await performSearch()
}

watch(() => filters.value, runTransactionalSearch, {
  immediate: true,
  deep: true,
})

useTransactionalFilterNavigationWatch({
  pageType: () => props.pageType,
  filters,
  categoryNameToSlug,
  categorySlug,
})
</script>

<style lang="postcss" scoped>
.filter-wrap {
  @apply flex md:flex-row gap-2 md:gap-0 py-4 max-md:overflow-x-auto max-md:overflow-y-visible;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none; /* For Chrome, Safari, and Edge */
  }
}
</style>
